import axios from 'axios';
import { client } from '@/services/api/axios.ts';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getImageUrlPlant } from '@/features/plants/hooks/queries/[plantId]/get-image-url.ts';
import { TPlant } from '@/features/plants/schemas/plants.schema.ts';

export type PatchPlantResponse = {
  body: TPlant;
};

export const usePatchPlantQuery = ({ onSuccess }: { onSuccess: (data: TPlant) => void }) => {
  const queryClient = useQueryClient();

  const updatePlantWithImages = async (plant: TPlant): Promise<TPlant> => {
    if (!plant.basicDetails.name || !plant.plantId || !plant.companyId) {
      throw new Error('Missing required plant information');
    }

    const { data } = await client.patch<PatchPlantResponse>(`/plants/${plant.plantId}`, {
      ...plant,
      siteInformation: {
        ...plant.siteInformation,
        image:
          typeof plant?.siteInformation?.image === 'string' ? plant.siteInformation.image : '',
      },
    });

    if (!data?.body?.plantId) {
      throw new Error('No plant ID returned from server.');
    }

    await uploadImages(plant.siteInformation.image, data.body.plantId);

    return data.body;
  };

  return useMutation({
    mutationFn: (plant: TPlant) =>
      toast.promise(updatePlantWithImages(plant), {
        loading: 'Updating plant...',
        success: 'Successfully updated the plant.',
        error: (_: unknown) => `Uh oh, there was an error with updating the plant.`,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['plants'] });

      if (data?.plantId) {
        queryClient.invalidateQueries({ queryKey: ['plants', data.plantId] });
      }

      if (data) {
        onSuccess(data);
      }
    },
  });
};

const uploadImages = async (images: unknown, plantId: string): Promise<void> => {
  if (!Array.isArray(images) || images.length === 0) return;

  const uploadPromises = images
    .filter((image): image is File => image instanceof File)
    .map(async (image) => {
      try {
        const response = await getImageUrlPlant({
          id: plantId,
          file: image,
        });

        if (response?.uploadUrl) {
          await axios.put(response.uploadUrl, image);
        }
      } catch (_: unknown) {
        toast.error(`Failed to upload plant image.`);
      }
    });

  await Promise.allSettled(uploadPromises);
};
